import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, Inject, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AcademyCommonModule, EnvironmentObject, PipesModule } from '@draeger-academy/common';
import { AcademyPresentationUiModule, FooterModule, HubspotTrackingService } from '@draeger-academy/presentation';
import { LocalizeParser, LocalizeRouterModule, LocalizeRouterSettings, ManualParserLoader } from '@gilsdav/ngx-translate-router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CountrySelectionModule } from 'views/country-selection/country-selection.module';
import { PageNotFoundModule } from '../../views/page-not-found/page-not-found.module';
import { BookingErrorModule } from '../../views/booking-error/booking-error.module';
import { ServerErrorPageModule } from '../../views/server-error-page/server-error-page.module';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ClickOutsideModule } from 'ng-click-outside';
import { MaintenanceOverlayModule } from '../../library/academy-presentation/lib/modules/maintenance-overlay';

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, `${environment.locales}assets/i18n/`, '.json');
}

export function createPartnerTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, `${environment.locales}assets/i18n/portals/`, '.json');
}

@NgModule({
    declarations: [AppComponent],
    exports: [],
    bootstrap: [AppComponent],
    imports: [
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FormsModule,
        AcademyCommonModule.forRoot(),
        AcademyPresentationUiModule.forRoot(),
        CommonModule,
        PipesModule,
        CountrySelectionModule,
        PageNotFoundModule,
        BookingErrorModule,
        ServerErrorPageModule,
        ClickOutsideModule,
        MaintenanceOverlayModule,
        FooterModule,
        // please do not change the order of the next 3 imports
        TranslateModule.forRoot({
            isolate: false,
            loader: {
                provide: TranslateLoader,
                useFactory: window.location.href.indexOf('partner') >= 0 ? createPartnerTranslateLoader : createTranslateLoader,
                deps: [HttpClient]
            }
        }),
        RouterModule.forRoot(routes, {}),
        LocalizeRouterModule.forRoot(routes, {
            defaultLangFunction: () => {
                const us = window.location.href.indexOf('_');
                const localizeDefaultLanguage = localStorage.getItem('LOCALIZE_DEFAULT_LANGUAGE');
                if (window.location.href.indexOf('partner') >= 0) {
                    return 'partner';
                } else if (us >= 0) {
                    if (window.location.href[us - 3] === '/') {
                        const array = window.location.href.split('/');
                        const location = array[3];
                        return location;
                    }
                } else if (localizeDefaultLanguage) {
                    return localizeDefaultLanguage === 'de_de' ? 'en_uk' : localizeDefaultLanguage;
                } else {
                    return 'en_uk';
                }
            },
            parser: {
                provide: LocalizeParser,
                useFactory: (translate, location, settings) =>
                    new ManualParserLoader(
                        translate,
                        location,
                        settings,
                        window.location.href.indexOf('partner') >= 0
                            ? []
                            : ['fr_fr', 'fr_ca', 'en_ca', 'de_at', 'en_uk', 'en_au', 'en_nz', 'fi_fi', 'da_dk', 'no_no', 'sv_se'],
                        'routes.'
                    ),
                deps: [TranslateService, Location, LocalizeRouterSettings]
            }
        })
    ],
    providers: [
        HubspotTrackingService,
        {
            provide: EnvironmentObject,
            useValue: {
                production: false
            }
        },
        // {
        //     provide: UrlSerializer,
        //     useClass: LowerCaseUrlSerializer
        // }
        { provide: APP_ID, useValue: 'serverApp' },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule {
    constructor(@Inject(PLATFORM_ID) private platformId: object, @Inject(APP_ID) private appId: string) {
        const platform = isPlatformBrowser(platformId) ? 'in the browser' : 'on the server';
        console.log(`Running ${platform} with appId=${appId}`);
    }
}
